import { useCallback, useEffect, useMemo, useState } from 'react'
import { Copy, Plus, Scissors, Trash2 } from 'lucide-react'

import { MasterFormModal } from '@/components/masters/MasterFormModal'
import { MasterServerTable } from '@/components/masters/MasterServerTable'
import { ServiceConsumablesPanel } from '@/components/masters/ServiceConsumablesPanel'
import { ServiceImageField } from '@/components/masters/ServiceImageField'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import { fetchServiceCategories } from '@/lib/api/serviceCategories'
import {
  createService,
  deleteService,
  duplicateService,
  fetchServicesList,
  fetchServicesPage,
  SKILL_LEVEL_OPTIONS,
  updateService,
} from '@/lib/api/services'
import { cn } from '@/lib/utils'

/**
 * @param {number | string | null | undefined} value
 */
function toNumber(value) {
  if (value === null || value === undefined || value === '') return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * @param {import('@/types/api').ServiceCategory | null} category
 */
function emptyForm(category) {
  return {
    service_category_id: category?.id ?? '',
    name: '',
    name_ar: '',
    duration: 30,
    price_ladies: '',
    price_gents: '',
    skill_level: 'standard',
    bookable_online: Boolean(category?.bookable_online_default ?? true),
    is_active: true,
  }
}

/**
 * @param {import('@/types/api').Service} service
 */
function serviceToForm(service) {
  return {
    service_category_id: service.service_category_id ?? '',
    name: service.name ?? '',
    name_ar: service.name_ar ?? '',
    duration: service.duration ?? 30,
    price_ladies: toNumber(service.price_ladies),
    price_gents: toNumber(service.price_gents),
    skill_level: service.skill_level ?? 'standard',
    bookable_online: Boolean(service.bookable_online),
    is_active: Boolean(service.is_active),
  }
}

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

function formatPrice(value) {
  const amount = toNumber(value)
  return amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name', getValue: (row) => row.name },
  {
    key: 'category',
    label: 'Category',
    getValue: (row) => row.category?.name ?? '',
  },
  { key: 'duration', label: 'Duration (min)', getValue: (row) => row.duration },
  { key: 'price_ladies', label: 'Price ladies', getValue: (row) => toNumber(row.price_ladies) },
  { key: 'price_gents', label: 'Price gents', getValue: (row) => toNumber(row.price_gents) },
  { key: 'name_ar', label: 'Arabic name', getValue: (row) => row.name_ar ?? '' },
  {
    key: 'bookable_online',
    label: 'Bookable online',
    getValue: (row) => (row.bookable_online ? 'yes' : 'no'),
  },
  { key: 'is_active', label: 'Active', getValue: (row) => (row.is_active ? 'yes' : 'no') },
]

export function ServicesPage() {
  const { activeBranchId } = useBranch()
  const [categories, setCategories] = useState([])
  const [filterCategoryId, setFilterCategoryId] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [selectedService, setSelectedService] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [duplicating, setDuplicating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState(emptyForm(null))
  const [reloadKey, setReloadKey] = useState(0)

  const extraParams = useMemo(() => {
    if (!filterCategoryId) return {}
    return { category_id: Number(filterCategoryId) }
  }, [filterCategoryId])

  const fetchPage = useCallback((params) => fetchServicesPage(params), [])

  useEffect(() => {
    let active = true
    setFilterCategoryId('')
    setSuccess('')
    setError('')

    async function loadCategories() {
      try {
        const items = await fetchServiceCategories()
        if (active) {
          setCategories(items)
        }
      } catch (err) {
        if (active) {
          setError(err?.response?.data?.message ?? 'Unable to load categories.')
        }
      }
    }

    loadCategories()
    return () => {
      active = false
    }
  }, [activeBranchId])

  function openCreate() {
    const category =
      categories.find((item) => String(item.id) === filterCategoryId) ?? categories[0] ?? null
    setCreating(true)
    setSelectedId(null)
    setSelectedService(null)
    setForm(emptyForm(category))
    setError('')
    setSuccess('')
    setModalOpen(true)
  }

  /**
   * @param {import('@/types/api').Service} service
   */
  function openEdit(service) {
    setCreating(false)
    setSelectedId(service.id)
    setSelectedService(service)
    setForm(serviceToForm(service))
    setError('')
    setSuccess('')
    setModalOpen(true)
  }

  function closeModal() {
    if (submitting || deleting) return
    setModalOpen(false)
    setError('')
    setSuccess('')
  }

  async function handleDuplicate() {
    if (!selectedId || creating) {
      return
    }

    setDuplicating(true)
    setError('')
    setSuccess('')

    try {
      const copy = await duplicateService(selectedId)
      setReloadKey((current) => current + 1)
      openEdit(copy)
      setSuccess('Service duplicated.')
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to duplicate service.')
    } finally {
      setDuplicating(false)
    }
  }

  async function handleDelete() {
    if (!selectedId || creating) {
      return
    }

    const serviceName = form.name.trim() || 'this service'
    if (
      !window.confirm(
        `Delete "${serviceName}"? This cannot be undone. The service will be removed from POS and booking menus. Past sales are kept but will no longer link to this service. Services linked to appointments or packages cannot be deleted — mark inactive instead.`,
      )
    ) {
      return
    }

    setDeleting(true)
    setError('')
    setSuccess('')

    try {
      await deleteService(selectedId)
      setReloadKey((current) => current + 1)
      setSuccess('Service deleted.')
      setModalOpen(false)
    } catch (err) {
      setError(
        getFieldError(err, 'service') ??
          err?.response?.data?.message ??
          'Unable to delete service.',
      )
    } finally {
      setDeleting(false)
    }
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
    setSuccess('')
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    const payload = {
      ...form,
      service_category_id: Number(form.service_category_id),
      duration: Number(form.duration) || 30,
      price_ladies: toNumber(form.price_ladies),
      price_gents: toNumber(form.price_gents),
      name_ar: form.name_ar || null,
    }

    try {
      if (creating) {
        await createService(payload)
        setSuccess('Service created.')
      } else if (selectedId) {
        await updateService(selectedId, payload)
        setSuccess('Service saved.')
      }
      setReloadKey((current) => current + 1)
      setModalOpen(false)
    } catch (err) {
      const message =
        getFieldError(err, 'name') ??
        getFieldError(err, 'service_category_id') ??
        getFieldError(err, 'duration') ??
        getFieldError(err, 'price_ladies') ??
        getFieldError(err, 'price_gents') ??
        err?.response?.data?.message ??
        'Unable to save service.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const hasCategories = categories.length > 0

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-8 lg:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Scissors className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Services</h1>
            <p className="text-sm text-muted-foreground">
              Menu items with dual pricing, duration, Arabic name, and online booking.
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={openCreate} disabled={!hasCategories}>
          <Plus className="h-4 w-4" />
          New service
        </Button>
      </div>

      {success && !modalOpen ? (
        <p className="mb-4 text-sm text-primary" role="status">
          {success}
        </p>
      ) : null}

      {error && !modalOpen ? (
        <p className="mb-4 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      {!hasCategories ? (
        <div className="card-surface p-6 text-sm text-muted-foreground">
          Create at least one service category before adding services.
        </div>
      ) : (
        <MasterServerTable
          key={activeBranchId}
          fetchPage={fetchPage}
          reloadKey={reloadKey}
          extraParams={extraParams}
          getRowKey={(service) => service.id}
          onEdit={openEdit}
          emptyLabel="No services yet."
          searchPlaceholder="Search services…"
          exportColumns={EXPORT_COLUMNS}
          exportFilename="services.csv"
          fetchAllForExport={(params) => fetchServicesList(params)}
          toolbar={
            <select
              value={filterCategoryId}
              onChange={(event) => setFilterCategoryId(event.target.value)}
              aria-label="Filter by category"
              className={cn(
                'h-10 min-w-[180px] rounded-xl border border-border/80 bg-card px-3 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
            >
              <option value="">All categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          }
          columns={[
            { key: 'name', label: 'Name', sortable: true, render: (row) => row.name },
            {
              key: 'category',
              label: 'Category',
              render: (row) => row.category?.name ?? '—',
            },
            {
              key: 'duration_minutes',
              label: 'Duration',
              sortable: true,
              render: (row) => `${row.duration} min`,
            },
            {
              key: 'price_ladies',
              label: 'Ladies',
              render: (row) => formatPrice(row.price_ladies),
            },
            {
              key: 'price_gents',
              label: 'Gents',
              render: (row) => formatPrice(row.price_gents),
            },
            {
              key: 'is_active',
              label: 'Status',
              render: (row) => (row.is_active ? 'Active' : 'Inactive'),
            },
          ]}
        />
      )}

      <MasterFormModal
        open={modalOpen}
        onClose={closeModal}
        title={creating ? 'New service' : 'Edit service'}
        description="Menu item with dual pricing, duration, Arabic name, and online booking."
        size="2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <ServiceImageField
            service={selectedService}
            disabled={submitting}
            onUpdated={(updated) => {
              setSelectedService(updated)
              setReloadKey((current) => current + 1)
              setSuccess('Service photo updated.')
            }}
          />

          <div>
            <label htmlFor="service-category" className="mb-1.5 block text-sm font-medium">
              Category
            </label>
            <select
              id="service-category"
              value={form.service_category_id}
              onChange={(e) => updateField('service_category_id', e.target.value)}
              className={cn(
                'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
              required
            >
              <option value="" disabled>
                Select category
              </option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="service-name" className="mb-1.5 block text-sm font-medium">
                Name (English)
              </label>
              <input
                id="service-name"
                type="text"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              />
            </div>
            <div>
              <label htmlFor="service-name-ar" className="mb-1.5 block text-sm font-medium">
                Name (Arabic)
              </label>
              <input
                id="service-name-ar"
                type="text"
                dir="rtl"
                value={form.name_ar}
                onChange={(e) => updateField('name_ar', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <div>
              <label htmlFor="service-duration" className="mb-1.5 block text-sm font-medium">
                Duration (min)
              </label>
              <input
                id="service-duration"
                type="number"
                min={5}
                max={1440}
                value={form.duration}
                onChange={(e) => updateField('duration', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              />
            </div>
            <div>
              <label htmlFor="service-price-ladies" className="mb-1.5 block text-sm font-medium">
                Price — ladies
              </label>
              <input
                id="service-price-ladies"
                type="number"
                min={0}
                step="0.01"
                value={form.price_ladies}
                onChange={(e) => updateField('price_ladies', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              />
            </div>
            <div>
              <label htmlFor="service-price-gents" className="mb-1.5 block text-sm font-medium">
                Price — gents
              </label>
              <input
                id="service-price-gents"
                type="number"
                min={0}
                step="0.01"
                value={form.price_gents}
                onChange={(e) => updateField('price_gents', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              />
            </div>
            <div>
              <label htmlFor="service-skill" className="mb-1.5 block text-sm font-medium">
                Skill level
              </label>
              <select
                id="service-skill"
                value={form.skill_level}
                onChange={(e) => updateField('skill_level', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              >
                {SKILL_LEVEL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.bookable_online}
              onChange={(e) => updateField('bookable_online', e.target.checked)}
              className="rounded border-border"
            />
            Bookable online
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => updateField('is_active', e.target.checked)}
              className="rounded border-border"
            />
            Service is active
          </label>

          {!creating && selectedId ? (
            <ServiceConsumablesPanel serviceId={selectedId} disabled={submitting} />
          ) : null}

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          {success ? (
            <p className="text-sm text-primary" role="status">
              {success}
            </p>
          ) : null}

          <div className="flex flex-wrap justify-between gap-2">
            {!creating && selectedId ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDuplicate}
                  disabled={duplicating || submitting || deleting}
                >
                  <Copy className="h-4 w-4" />
                  {duplicating ? 'Duplicating…' : 'Duplicate'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDelete}
                  disabled={deleting || submitting || duplicating}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  {deleting ? 'Deleting…' : 'Delete service'}
                </Button>
              </div>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={closeModal} disabled={submitting || deleting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || deleting}>
                {submitting ? 'Saving…' : creating ? 'Create service' : 'Save service'}
              </Button>
            </div>
          </div>
        </form>
      </MasterFormModal>
    </div>
  )
}
